import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';

import type { Principal } from '../shared/index.js';
import { userId } from '../shared/index.js';

import { ApiError, AUTH_LOST_EVENT } from '../api/client.js';
import type { SessionUser } from '../api/endpoints.js';
import { authApi } from '../api/endpoints.js';
import { queryKeys } from '../app/query-client.js';

interface SessionValue {
  readonly user: SessionUser | null;
  readonly principal: Principal | null;
  readonly isLoading: boolean;
  readonly signIn: (email: string, password: string) => Promise<void>;
  readonly signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

function toPrincipal(user: SessionUser): Principal {
  return {
    id: userId(user.id),
    jobRoleSlug: user.jobRoleSlug,
    jobRoleId: user.jobRoleId,
    overlays: user.overlays,
    departments: user.departments.map((entry) => ({
      departmentId: entry.departmentId as Principal['departments'][number]['departmentId'],
      slug: entry.slug,
      level: entry.level,
    })),
    managedProjectIds: user.managedProjectIds as Principal['managedProjectIds'],
  };
}

export function SessionProvider({ children }: { children: ReactNode }): ReactNode {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.session,
    queryFn: async () => {
      try {
        return await authApi.me();
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 60_000,
  });

  useEffect(() => {
    const onAuthLost = (): void => {
      queryClient.setQueryData(queryKeys.session, null);
    };

    window.addEventListener(AUTH_LOST_EVENT, onAuthLost);
    return () => {
      window.removeEventListener(AUTH_LOST_EVENT, onAuthLost);
    };
  }, [queryClient]);

  const signIn = useCallback(
    async (email: string, password: string): Promise<void> => {
      const user = await authApi.login(email, password);
      queryClient.setQueryData(queryKeys.session, user);
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== queryKeys.session[0],
      });
    },
    [queryClient],
  );

  const signOut = useCallback(async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
    }

    queryClient.setQueryData(queryKeys.session, null);
  }, [queryClient]);

  const value = useMemo<SessionValue>(() => {
    const user = data ?? null;
    const principal = user === null ? null : toPrincipal(user);

    return {
      user,
      principal,
      isLoading,
      signIn,
      signOut,
    };
  }, [data, isLoading, signIn, signOut]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);

  if (value === null) {
    throw new Error('useSession must be used inside SessionProvider.');
  }

  return value;
}
